import fs from 'fs/promises';
import path from 'path';
import { fileURLToPath } from 'url';
import { parse } from '@babel/parser';
import type {
  Node as BabelNode,
  JSXIdentifier,
  JSXMemberExpression,
  JSXOpeningElement,
} from '@babel/types';
import MagicString from 'magic-string';
import type { Plugin } from 'vite';

import { DATA_ATTRIBUTES } from './constants';

const validExtensions = new Set(['.jsx', '.tsx']);

export function cartographer(): Plugin {
  let clientScript: string;
  let configuredRoot: string;
  let configuredRootName: string;

  return {
    name: '@replit/vite-plugin-cartographer',
    enforce: 'pre' as const,

    async configResolved(config) {
      configuredRoot = config.root;
      // The name of the directory that contains the configured root
      configuredRootName = path.basename(configuredRoot);

      // Resolve the client script path in both CJS and ESM environments
      const currentFileUrl =
        typeof __dirname === 'string'
          ? path.join(__dirname, '../dist/beacon/index.global.js')
          : fileURLToPath(
              new URL('../dist/beacon/index.global.js', import.meta.url),
            );

      try {
        clientScript = await fs.readFile(currentFileUrl, 'utf-8');
      } catch (error) {
        // eslint-disable-next-line no-console -- This is an error in the plugin
        console.error(
          '[replit-cartographer] Failed to load client script:',
          error,
        );
      }
    },

    resolveId(_source, _importer) {
      // Let Vite handle the resolution
      return null;
    },

    transform: {
      order: 'pre',
      async handler(code: string, id: string) {
        if (
          !validExtensions.has(path.extname(id)) ||
          id.includes('node_modules')
        ) {
          return null;
        }

        try {
          const ast = parse(code, {
            sourceType: 'module',
            plugins: ['jsx', 'typescript'],
          });

          const magicString = new MagicString(code);
          let currentElement: BabelNode | null = null;

          // One day the JS ecosystem will decide on one standard to use, until then
          // We need to dynamically import this so that we don't mess with vite resolving this module
          const traverse = await import('@babel/traverse').then(
            (m) => m.default,
          );

          traverse(ast, {
            JSXElement: {
              enter(elementPath) {
                currentElement = elementPath.node;
              },
              exit() {
                currentElement = null;
              },
            },
            JSXOpeningElement(elementPath) {
              if (currentElement) {
                const jsxNode = elementPath.node;
                const elementName = getElementName(jsxNode);

                if (!elementName) {
                  return;
                }

                const { line = 0, column: col = 0 } = jsxNode.loc?.start ?? {};

                const relativeToConfigured = path.relative(configuredRoot, id);
                const componentPath = path.join(
                  configuredRootName,
                  relativeToConfigured,
                );

                const componentMetadata =
                  col === 0
                    ? `${componentPath}:${line}`
                    : `${componentPath}:${line}:${col}`;

                magicString.appendLeft(
                  jsxNode.name.end ?? 0,
                  ` ${DATA_ATTRIBUTES.METADATA}="${componentMetadata}" ${DATA_ATTRIBUTES.COMPONENT_NAME}="${elementName}"`,
                );
              }
            },
          });

          return {
            code: magicString.toString(),
            map: magicString.generateMap({ hires: true }),
          };
        } catch (error) {
          // eslint-disable-next-line no-console -- I want my errors to be logged :D
          console.error(`[replit-cartographer] Error processing ${id}:`, error);

          return null;
        }
      },
    },

    transformIndexHtml() {
      if (!clientScript) {
        return [];
      }

      return [
        {
          tag: 'script',
          attrs: { type: 'module' },
          children: clientScript,
          injectTo: 'head',
        },
      ];
    },
  };
}

// Helper function to extract element name from JSX node
function getElementName(jsxNode: JSXOpeningElement): string | null {
  if (jsxNode.name.type === 'JSXIdentifier') {
    return jsxNode.name.name;
  }

  if (jsxNode.name.type === 'JSXMemberExpression') {
    const memberExpr = jsxNode.name as JSXMemberExpression;
    const object = memberExpr.object as JSXIdentifier;
    const property = memberExpr.property as JSXIdentifier;

    return `${object.name}.${property.name}`;
  }

  return null;
}
